import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";

interface ProductReviewsProps {
  productId: string;
}

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  user_id: string;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

const ProductReviews = ({ productId }: ProductReviewsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Récupérer les avis
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["reviews", productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_reviews")
        .select("*")
        .eq("product_id", productId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Récupérer les profils séparément
      const reviewsWithProfiles = await Promise.all(
        (data || []).map(async (review) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name")
            .eq("id", review.user_id)
            .single();

          return {
            ...review,
            profiles: profile || { first_name: null, last_name: null }
          };
        })
      );

      return reviewsWithProfiles as Review[];
    },
  });

  // Vérifier si l'utilisateur a acheté le produit
  const { data: hasPurchased } = useQuery({
    queryKey: ["hasPurchased", productId, user?.id],
    queryFn: async () => {
      if (!user) return false;

      const { data, error } = await supabase
        .from("order_items")
        .select("id")
        .eq("product_id", productId)
        .limit(1);

      if (error) {
        console.error("Error checking purchase:", error);
        return false;
      }
      
      if (!data || data.length === 0) return false;

      // Vérifier si l'utilisateur a une commande complétée avec ce produit
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select("id, status")
        .eq("user_id", user.id)
        .neq("status", "pending");

      if (ordersError) {
        console.error("Error checking orders:", ordersError);
        return false;
      }

      return orders && orders.length > 0;
    },
    enabled: !!user,
  });

  // Vérifier si l'utilisateur a déjà laissé un avis
  const { data: userReview } = useQuery({
    queryKey: ["userReview", productId, user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("product_reviews")
        .select("*")
        .eq("product_id", productId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Soumettre un avis
  const submitReview = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Vous devez être connecté");
      if (!rating) throw new Error("Veuillez sélectionner une note");

      const { error } = await supabase.from("product_reviews").insert({
        product_id: productId,
        user_id: user.id,
        rating,
        comment: comment.trim() || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", productId] });
      queryClient.invalidateQueries({ queryKey: ["userReview", productId, user?.id] });
      setRating(0);
      setComment("");
      setShowReviewForm(false);
      toast({
        title: "Avis publié",
        description: "Merci pour votre avis !",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });

  const renderStars = (count: number, interactive = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= (interactive ? hoveredRating || rating : count)
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            } ${interactive ? "cursor-pointer" : ""}`}
            onClick={() => interactive && setRating(star)}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
          />
        ))}
      </div>
    );
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
    : "0";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Avis clients</CardTitle>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-bold">{averageRating}</div>
            {renderStars(Math.round(parseFloat(averageRating)))}
            <div className="text-sm text-muted-foreground">
              {reviews.length} {reviews.length > 1 ? "avis" : "avis"}
            </div>
          </div>
        </CardHeader>

        {user && hasPurchased && !userReview && (
          <CardContent>
            {!showReviewForm ? (
              <Button onClick={() => setShowReviewForm(true)}>
                Laisser un avis
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Votre note
                  </label>
                  {renderStars(rating, true)}
                </div>

                <div>
                  <label htmlFor="comment" className="text-sm font-medium mb-2 block">
                    Votre commentaire (optionnel)
                  </label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Partagez votre expérience avec ce produit..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => submitReview.mutate()}
                    disabled={!rating || submitReview.isPending}
                  >
                    {submitReview.isPending ? "Publication..." : "Publier l'avis"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowReviewForm(false);
                      setRating(0);
                      setComment("");
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Chargement des avis...
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Aucun avis pour le moment. Soyez le premier à donner votre avis !
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review, index) => (
            <div key={review.id}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {review.profiles?.first_name && review.profiles?.last_name
                        ? `${review.profiles.first_name} ${review.profiles.last_name}`
                        : "Utilisateur"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  {renderStars(review.rating)}
                </div>
                {review.comment && (
                  <p className="text-muted-foreground">{review.comment}</p>
                )}
              </div>
              {index < reviews.length - 1 && <Separator className="my-4" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
